// Buttons in every weight.

import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes } from "react";
import { cx } from "@/platform/ui/primitives/cx.js";
import { IconSettings } from "@/platform/ui/icons.js";

// ---- Button ----
type ButtonLook = {
  variant?: "primary" | "ghost" | "subtle" | "danger" | "positive";
  size?: "sm" | "md";
};

function buttonClass({ variant = "subtle", size = "md" }: ButtonLook, className?: string) {
  const variants = {
    primary: "bg-accent text-bg hover:bg-accent-strong font-semibold",
    ghost: "bg-transparent text-dim hover:text-ink hover:bg-surface-2",
    subtle: "bg-surface-2 text-ink-soft hover:bg-raised border border-line",
    danger: "bg-transparent text-negative hover:bg-negative/10 border border-negative/30",
    positive: "bg-transparent text-positive hover:bg-positive/10 border border-positive/30",
  }[variant];
  const sizes = { sm: "px-2.5 py-1.5 text-xs", md: "px-3.5 py-2 text-sm" }[size];
  return cx(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
    variants,
    sizes,
    className,
  );
}

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & ButtonLook
>(function Button({ variant, size, className, children, ...rest }, ref) {
  return (
    <button ref={ref} className={buttonClass({ variant, size }, className)} {...rest}>
      {children}
    </button>
  );
});

/** A link in a `Button`'s shape: a download. */
export function ButtonLink({
  variant,
  size,
  className,
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & ButtonLook) {
  return (
    <a className={buttonClass({ variant, size }, className)} {...rest}>
      {children}
    </a>
  );
}

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  variant?: "ghost" | "subtle" | "danger";
  size?: "sm" | "md";
};

export function IconButton({
  label,
  variant = "ghost",
  size = "md",
  className,
  children,
  ...rest
}: IconButtonProps) {
  const variants = {
    ghost: "border-transparent bg-transparent text-muted hover:bg-surface-2 hover:text-ink",
    subtle: "border-line bg-surface-2 text-ink-soft hover:bg-raised",
    danger: "border-transparent bg-transparent text-muted hover:bg-negative/10 hover:text-negative",
  }[variant];
  const sizes = { sm: "h-7 w-7 rounded-md", md: "h-8 w-8 rounded-lg" }[size];
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        "inline-flex flex-none items-center justify-center border transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        variants,
        sizes,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

const compactButtonShapeClass =
  "inline-flex flex-none items-center justify-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50";
const compactButtonColorClass = "border-line bg-surface text-dim hover:text-ink";

const compactButtonClass = cx(compactButtonShapeClass, compactButtonColorClass);

const compactIconButtonClass =
  "inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-lg border border-line bg-surface text-muted transition-colors hover:text-ink disabled:cursor-not-allowed disabled:opacity-50";

export function CompactButton({
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={cx(compactButtonClass, className)} {...rest}>
      {children}
    </button>
  );
}

// A single independent boolean filter chip — on/off, not a mode (SegmentedControl) and not
// multi-value (MultiSelectFilter). `pressed` drives both the highlight and aria-pressed from one
// value, so a screen can't end up with a control that looks unpressed but reports pressed (or
// vice versa).
//
// Deliberately doesn't wrap CompactButton: CompactButton bakes its own border/bg/text colors into
// a single class, and layering a conditional accent className on top of that (via cx, a bare
// string-joiner) put both color sets in the class list at once, letting the cascade — not
// `pressed` — decide the winner. The two color sets are chosen with a ternary instead, so only
// one is ever present in the DOM.
export function FilterToggle({
  pressed,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { pressed: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className={cx(
        compactButtonShapeClass,
        pressed ? "border-accent bg-accent-soft text-accent" : compactButtonColorClass,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function CompactIconButton({
  label,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(compactIconButtonClass, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---- ManageButton ----
// The one way a page offers "manage the things behind this screen", so the
// affordance looks the same wherever it is met.
//
// Button/subtle rather than CompactButton: design-system.md reserves the
// compact variants for a dense toolbar, and a page header is not one.
// IconSettings rather than a per-page glyph: one icon per meaning
// (platform/ui/icons.tsx).
export function ManageButton({
  label = "Manage",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label?: string }) {
  return (
    <Button size="sm" variant="subtle" {...rest}>
      <IconSettings size={13} /> {label}
    </Button>
  );
}
